import React, {useEffect, useReducer, useState} from 'react'
import Location from './Location/Location'
import Document from './Document/Document'
import VisitorsList from '../VisitorsList/VisitorsList'
import Confirmation from '../../../Confirmation/Confirmation'
import styled from 'styled-components'
import isFunction from 'lodash/isFunction'
import {submitServiceRequest} from '../../../../api/servicerequest'
import Equipment from './Equipment/Equipment'
import Header from '../../../Header/Header'
import HeaderTitle from '../../../Header/HeaderTitle'
import HeaderButton from '../../../Header/HeaderButton'
import {useUserState} from '../../../../context/UserContext'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import {DASHBOARD, KIOSK_CONTAINER} from '../../../../constants/const'
import {useKioskTimer} from '../../../../hooks/kiosk-timer'
import {useUIState, useUIDispatch} from '../../../../context/UIContext'
import RoomSelect from '../../../../pages/RoomSelect/RoomSelect'
import MediaQuery from 'react-responsive'
import {fetchFloor} from '../../../../api/floor'
import {getServiceRequestVisibleFields} from '../../../../utils/servicerequests'

export const REQUEST_TYPE = 'REQUEST_TYPE'
export const LOCATION = 'LOCATION'
export const VISITORS_LIST = 'VISITORS_LIST'
export const DOCUMENT = 'DOCUMENT'
export const CONFIRMATION = 'CONFIRMATION'
export const EQUIPMENT = 'EQUIPMENT'
export const ROOM = 'ROOM'

const initialState = {
  building: {
    value: '',
    name: ''
  },
  floor: {
    value: '',
    name: ''
  },
  room: {
    value: '',
    name: ''
  },
  equipment: {
    value: '',
    name: ''
  },
  priority: {}, // No longer used. Removed in Service Catalog changes
  requester: '',
  requesterPhone: '',
  description: '',
  location: '',
  activityType: '',
  errors: {
    description: null,
    equipment: null,
    building: null
  },
  touched: {
    description: false,
    equipment: false,
    building: false
  },
  doc1: '',
  doc2: '',
  doc3: '',
  doc4: '',
  dv_id: '',
  dp_id: '',
  siteId: '',
  isSubmitting: false,
  submitError: null,
  probType: null
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_VALUE':
      return {
        ...state,
        ...action.payload
      }
    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      }
    case 'SUBMIT': {
      return {
        ...state,
        isSubmitting: true
      }
    }
    case 'SUBMIT_SUCCESS': {
      return {
        ...state,
        isSubmitting: false,
        submitError: null
      }
    }
    case 'SUBMIT_ERROR': {
      return {
        ...state,
        isSubmitting: false,
        submitError: action.payload
      }
    }
    case 'SET_FORM_STATE': {
      return {
        ...state,
        ...action.payload
      }
    }
    case 'RESET_FORM': {
      return {
        ...state,
        ...initialState
      }
    }
  }
}

const useForm = ({initialState, reducer, submitFn, user, localeState}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const handleSubmit = () => {
    dispatch({type: 'SUBMIT'})

    let data = state

    if (!state.requester) {
      data = {...state, ...{requester: user.employee.id}}
    }

    return submitFn(data)
      .then(() => {
        dispatch({type: 'SUBMIT_SUCCESS'})
        return Promise.resolve()
      })
      .catch(error => dispatch({type: 'SUBMIT_ERROR', payload: error}))
  }

  const handleChange = value => {
    let [fieldName] = Object.keys(value)
    let payload = {errors: {...state.errors, [fieldName]: null}, ...value}

    dispatch({type: 'SET_VALUE', payload})
  }

  // TODO: Move validate outside of hook
  const validate = () => {
    const {description, building} = state

    return {
      description:
        description.length > 0
          ? null
          : _L({key: 'Description is required', localeState}),
      building:
        building.value.length > 0
          ? null
          : _L({key: 'Building is required', localeState})
    }

  }
    
  const validateDoc = () => {
	  const {requesterPhone} = state
	  
	  return{
		  requesterPhone:
	      	  requesterPhone.length > 0
	            ? null
	            : _L({key: 'Email is required', localeState})
	  }
  }
  const handleError = ({errors}) =>
    dispatch({type: 'SET_ERROR', payload: errors})

  const setFormState = ({formState}) =>
    dispatch({type: 'SET_FORM_STATE', payload: formState})

  const resetForm = () => dispatch({type: 'RESET_FORM'})

  return {
    state,
    handleSubmit,
    handleChange,
    validate,
    validateDoc,
    handleError,
    setFormState,
    resetForm
  }
}

const ServiceSequence = ({
  onComplete,
  isEquipmentQR,
  isRoomQR,
  eqId,
  onHeaderAction,
  initialForm,
  activityType,
  probType,
  parentPage,
  catalogHeader,
  hideFields,
  instructions
}) => {
  const {user, defaultLocation} = useUserState()
  const localeState = useLocaleState()
  const {homeFloor, isKiosk} = useUIState()
  const uiDispatch = useUIDispatch()
  const [showRoomSelect, setShowRoomSelect] = useState(false)
  const [visibleFields, setVisibleFields] = useState({
    hasLocation: true,
    hasEquipment: true,
    hasSpecificLocation: true,
    hasDoc1: true,
    hasDoc2: true,
    hasDoc3: true,
    hasDoc4: true
  })

  useEffect(
    () => {
      if (initialForm) {
        //const {bl, fl, rm} = defaultLocation
        const formState = {
          building: {
            value:  '',
            name:  ''
          },
          floor: {
            value: '',
            name:  ''
          },
          room: {
            value: '',
            name: ''
          },
          activityType: activityType || null,
          probType: probType || null,
          requesterPhone: user.employee.phone || ''
        }

        setFormState({formState})
        document.getElementsByTagName("TITLE")[0].innerHTML = "UIUC Facility Access Maps | " + catalogHeader + " Form"
      }
    },
    [defaultLocation, initialForm, activityType, probType]
  )

  useEffect(
    () => {
      if (hideFields) {
        setVisibleFields(getServiceRequestVisibleFields(hideFields))
      }
    },
    [hideFields]
  )

  const timeoutExpired = () => {
    uiDispatch({type: 'setContent', payload: {content: KIOSK_CONTAINER}})
    uiDispatch({type: 'setActiveFloor', payload: homeFloor})
  }

  const {message, onClickHandler} = useKioskTimer({
    enable: isKiosk,
    inUseDelay: 60000,
    timeoutDelay: 10000,
    timeoutExpirationHandler: timeoutExpired
  })

  const getConfirmationNext = () => {
    let next = null

    if (isEquipmentQR) {
      next = EQUIPMENT
    }
    if (isRoomQR) {
      next = ROOM
    }

    if (isKiosk) {
      next = 'CONFERENCE_ROOM'
    }

    return next
  }

  // TODO: should next and prev always be functions?
  const viewStates = {
    REQUEST_TYPE: {
      next: LOCATION,
      prev: null
    },
    EQUIPMENT: {
      next: LOCATION,
      prev: null
    },
    LOCATION: {
      next: user.guest ? VISITORS_LIST : DOCUMENT,
      prev: () => {
        if (
          parentPage === DASHBOARD ||
          parentPage === 'SERVICE_CATALOG' ||
          parentPage === 'ROOM_QR' ||
          parentPage === 'KIOSK_CONTAINER'
        ) {
          uiDispatch({
            type: 'setContent',
            payload: {content: parentPage}
          })
          return null
        } else {
          if (isEquipmentQR) {
            setCurrentView(EQUIPMENT)
          }
        }
      },
      errorKeys: ['equipment', 'building', 'description']
    },
    VISITORS_LIST: {
      next: DOCUMENT,
      prev: LOCATION
    },
    CONFIRMATION: {
      next: () => {
        let nextView = getConfirmationNext()
        setCurrentView(nextView)
        // Reset
        resetForm()
      },
      prev: DOCUMENT
    },
    DOCUMENT: {
      prev: user.guest ? VISITORS_LIST : LOCATION,
      next: () => {
        handleSubmit().then(() => {
          setCurrentView(CONFIRMATION)
        })
      },
      errorKeys: ['requesterPhone']
    }
  }

  const {
    state,
    handleSubmit,
    handleChange,
    validate,
    validateDoc,
    handleError,
    setFormState,
    resetForm
  } = useForm({
    initialState,
    reducer,
    submitFn: submitServiceRequest,
    user,
    localeState
  })

  const getInitialView = () => {
    if (initialForm) {
      return initialForm
    }

    let view = REQUEST_TYPE

    if (isEquipmentQR) {
      view = EQUIPMENT
    }

    if (isRoomQR) {
      view = ROOM
    }

    return view
  }
  // Navigation state
  const [currentView, setCurrentView] = useState(getInitialView())

  const goToMain = evt => {
	if(evt.key == "Enter" || evt.key == " " || !evt.key){
	    if (isEquipmentQR || isRoomQR) {
	      getNextView({action: 'next'})
	    } else {
	    	onHomeClick()
	    }
	}
	if(evt.key == "Escape"){
		
	}
  }

  const getNextView = ({action = null} = {}) => {
    let anyError = false
    // Check currentView for validation errors before proceeding
    if (viewStates[currentView].errorKeys && action === 'next') {
      let errors = ''
      if (currentView == 'DOCUMENT'){
    	  errors = validateDoc()
      }else{
    	  errors = validate()
      }
      const errorFieldsForView = viewStates[currentView].errorKeys

      let errorsForView = errorFieldsForView.reduce((acc, item) => {
        acc[item] = errors[item]
        return acc
      }, {})

      anyError = Object.keys(errorsForView).some(x => errorsForView[x])
      handleError({errors: errorsForView})
    }

    // Clear errors on current view when going back to the previous view
    if (viewStates[currentView].errorKeys && action === 'prev') {
      const errorFieldsForView = viewStates[currentView].errorKeys
      let errorsForView = errorFieldsForView.reduce((acc, item) => {
        acc[item] = null
        return acc
      }, {})

      handleError({errors: errorsForView})
    }

    // No errors, change the view
    if (!anyError) {
      let nextState = currentView
      if (action) {
        nextState = viewStates[currentView][action]
      }

      if (nextState !== currentView) {
        if (isFunction(nextState)) {
          nextState()
        } else {
          setCurrentView(nextState)
        }
      }
    }
  }

  const onSelectVisitor = ({emId: em_id, dvId: dv_id, dpId: dp_id}) => {
    handleChange({requester: em_id, dv_id, dp_id})
    getNextView({action: 'next'})
  }

  const onSelectRoom = ({room}) => {
    if (room) {
      const formState = {
        building: {
          value: room.blId,
          name: room.blName
        },
        floor: {
          value: room.flId,
          name: room.flName
        },
        room: {
          value: room.rmId,
          name: room.name || room.rmTypeDesc
        }
      }
      setFormState({formState})
    }
    setShowRoomSelect(false)
  }

  const createRequestFromEquipment = equipment => {
    const {name, blId, flId, flName, rmId, rmName, eqId} = equipment

    const formState = {
      building: {
        value: blId,
        name: name
      },
      floor: {
        value: flId,
        name: flName
      },
      room: {
        value: rmId,
        name: rmName
      },
      equipment: {
        value: eqId,
        name: eqId
      }
    }

    setFormState({formState})

    getNextView({action: 'next'})
  }

  const renderContent = () => {
    const views = {
      LOCATION: (
        <Location
          setAction={getNextView}
          setValue={handleChange}
          parentState={state}
          isQrRequest={isEquipmentQR || isRoomQR}
          onRoomSelect={onRoomSelect}
          visibleFields={visibleFields}
          instructions={instructions}
          title={catalogHeader}
        />
      ),
      VISITORS_LIST: (
        <VisitorsList
          onSelectVisitor={onSelectVisitor}
          label={_L({key: 'Requested by:', localeState})}
          employeesOnly
        />
      ),
      DOCUMENT: (
        <Document
          setAction={getNextView}
          setValue={handleChange}
          parentState={state}
          visibleFields={visibleFields}
        />
      ),
      CONFIRMATION: (
        <Confirmation
          title={_L({key: 'Service Request has been created.', localeState})}
          goToMain={goToMain}
        />
      ),
      EQUIPMENT: (
        <Equipment eqId={eqId} createRequest={createRequestFromEquipment} />
      )
    }

    return views[currentView]
  }

  const onRoomSelect = async () => {
    // Only set the active floor if the building and room fields are
    // populated
    const {building, floor} = state
    let blId = building.value
    let flId = floor.value
    let location = {blId, flId}

    if (blId && !flId) {
      // Get the first floor for the building
      const floor = await fetchFloor({blId})
      if (floor) {
        location.flId = floor
      }
    }

    if (location.blId && location.flId) {
      uiDispatch({
        type: 'setActiveFloor',
        payload: location
      })

      setShowRoomSelect(true)
    }
  }
  const onHomeClick = () => {
	    uiDispatch({
	      type: 'showSearch',
	      payload: {
	        show: false,
	        type: null,
	        options: {},
	        loaded: false
	      }
	    })
	    uiDispatch({type: 'setActiveFloor', payload: homeFloor})

	    if (isKiosk) {
	      setShowDepartmentList(true)
	      setShowDetail(false)
	    } else {
	      uiDispatch({type: 'setContent', payload: {content: DASHBOARD}})
	    }
	  }
  
  const onHeaderCancel = evt => {
    if(evt.key == "Enter" || evt.key == " " || !evt.key){
      if (isEquipmentQR) {
        setCurrentView(EQUIPMENT)
      }

      onHomeClick()
    }
  }

  const onHeaderSignOn = () => onHeaderAction({type: 'SIGN_ON'})

  const onHeaderHome = () => onHeaderAction({type: 'HOME'})

  const isQrView = isEquipmentQR || isRoomQR
  const isRoomView = currentView === ROOM
  const isGuest = user.guest

  const isEquipmentHomeView = isEquipmentQR && currentView === 'EQUIPMENT'

  const signOnLabel = isGuest
    ? _L({key: 'Sign On', localeState})
    : _L({key: 'Sign Off', localeState})

  const renderHeaderButton = () => (
    <HeaderButtons>
      {isQrView && (
        <HeaderButton onClick={onHeaderSignOn} label={signOnLabel} />
      )}
      {isQrView &&
        !isGuest &&
        isRoomView && (
          <HeaderButton
            onClick={onHeaderHome}
            label={_L({key: 'Home', localeState})}
          />
        )}
      {!isRoomView &&
        !isEquipmentHomeView && (
          <HeaderButton
            onClick={onHeaderCancel}
            onKeyDown={onHeaderCancel}
            label={_L({key: 'Cancel', localeState})}
          />
        )}
    </HeaderButtons>
  )

  const createRequestText = _L({key: 'Create Request', localeState})

  return showRoomSelect ? (
    <RoomSelect selectedRoomId={state.room.value} onSelectRoom={onSelectRoom} />
  ) : (
    <Container onClick={onClickHandler}>
      <MediaQuery maxWidth={420}>
        {matches => (
          <header role="banner">
	          <Header
	            leftContent= <SiteNameText role="link" onClick={onHeaderCancel} onKeyDown={onHeaderCancel} tabIndex="0">Facility Access Maps</SiteNameText>
	
	            rightContent={renderHeaderButton()}
	          />
          </header>
        )}
      </MediaQuery>
      <MainContent role="main">
      	<StyledContent>{renderContent()}</StyledContent>
      </MainContent>
      {message}
    </Container>
  )
}

export default ServiceSequence

ServiceSequence.defaultProps = {
  isEquipmentQR: false,
  isRequestComplete: false,
  isRoomQR: false
}

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #eff0f3;
  box-sizing: border-box;
`
const MainContent = styled.main`
  display: block;
`

const StyledContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100vw;
  height: calc(100vh - 110px);
  background-color: #eff0f3;
  box-sizing: border-box;
  overflow-y: auto;
  padding: 34px 10px 40px 10px;

  @media (max-width: 468px) {
    padding: 10px;
  }
  
  @media (max-width: 420px) {
    padding: 0;
    overflow-y: hidden;
  }
`

const HeaderButtons = styled.div`
  box-sizing: border-box;
`

const SiteNameText = styled.h1`
  font-weight: 700;
  color: #eff0f4;
  font-size: 30px;
  letter-spacing: -0.65px;
  cursor: pointer;
  padding: 5px;
  
  @media (max-width: 768px) {
    font-size: 26px;
    padding: 0px;
  }
  
  :focus { 
  	outline: 3px solid #FF9900;
  }
`